import { useEffect, useState } from "react";
import type { AiReport } from "@/types";
import { useVerdict } from "@/hooks/useVerdict";

interface DamageVerdictProps {
  bookingId: number;
  aiReport?: AiReport | null;
}

const MAX_NOTE = 400;

export default function DamageVerdict({ bookingId, aiReport }: DamageVerdictProps) {
  const { verdict, loading, error, saving, save, resolve } = useVerdict(bookingId);
  const [editing, setEditing] = useState(false);
  const [draftFlagged, setDraftFlagged] = useState(false);
  const [draftNote, setDraftNote] = useState("");
  const [saveError, setSaveError] = useState("");

  useEffect(() => {
    if (!editing && verdict) {
      setDraftFlagged(verdict.flagged);
      setDraftNote(verdict.note ?? "");
    }
  }, [verdict, editing]);

  const startEditing = (flagged: boolean, note: string) => {
    setDraftFlagged(flagged);
    setDraftNote(note);
    setSaveError("");
    setEditing(true);
  };

  const commit = async () => {
    setSaveError("");
    try {
      await save({ flagged: draftFlagged, note: draftNote });
      setEditing(false);
    } catch (err) {
      setSaveError(err instanceof Error ? err.message : "We couldn't save the verdict.");
    }
  };

  const aiSuggests = !!aiReport?.damage_suspected;

  return (
    <div className="mt-4 rounded-xl border border-background-200/70 bg-background-100 p-4">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <p className="flex items-center gap-2 text-sm font-medium text-foreground-950">
          <i className="ri-flag-2-line text-secondary-400"></i> Damage verdict
        </p>
        {!loading && verdict && !editing && (
          <div className="flex flex-wrap items-center gap-2">
            <span
              className={`inline-flex items-center gap-1.5 whitespace-nowrap rounded-full px-2.5 py-1 text-[11px] font-medium ${
                verdict.flagged ? "bg-secondary-500 text-background-50" : "bg-accent-100 text-accent-900"
              }`}
            >
              <i className={verdict.flagged ? "ri-alert-line" : "ri-shield-check-line"}></i>
              {verdict.flagged ? "Damage flagged" : "No damage flagged"}
            </span>
            {verdict.resolved && (
              <span className="whitespace-nowrap rounded-full bg-background-200/60 px-2.5 py-1 text-[11px] text-foreground-600">
                Resolved
              </span>
            )}
          </div>
        )}
      </div>

      {loading && <div className="mt-3 h-10 w-full animate-pulse rounded-lg bg-background-200/40" />}

      {!loading && error && (
        <p className="mt-3 flex items-center gap-2 rounded-lg border border-secondary-700/40 bg-secondary-950/40 px-3 py-2 text-xs text-secondary-300">
          <i className="ri-error-warning-line"></i> {error}
        </p>
      )}

      {!loading && !error && !editing && (
        <div className="mt-3">
          {verdict ? (
            <>
              <p className="text-sm leading-relaxed text-foreground-700">
                {verdict.note || "No dispute note was added."}
              </p>
              <p className="mt-1 text-[11px] text-foreground-500">
                Logged by {verdict.flagged_by_name || "a Gari user"}
              </p>
              <div className="mt-3 flex flex-wrap gap-2">
                <button
                  type="button"
                  onClick={() => startEditing(!verdict.flagged, verdict.note ?? "")}
                  className="inline-flex items-center gap-1.5 whitespace-nowrap rounded-md border border-background-200 bg-background-50 px-3.5 py-2 text-xs font-medium text-foreground-900 transition-colors hover:border-primary-500/60"
                >
                  <i className="ri-edit-line"></i> Edit verdict
                </button>
                {verdict.flagged && !verdict.resolved && (
                  <button
                    type="button"
                    onClick={resolve}
                    className="inline-flex items-center gap-1.5 whitespace-nowrap rounded-md bg-accent-500 px-3.5 py-2 text-xs font-medium text-background-50 transition-colors hover:bg-accent-600"
                  >
                    <i className="ri-check-double-line"></i> Mark resolved
                  </button>
                )}
              </div>
            </>
          ) : (
            <>
              <p className="text-xs leading-relaxed text-foreground-600">
                Compare both walkarounds above, then record the verdict. {aiSuggests ? "AI spotted something worth a look — " : ""}
                flag it and add a short dispute note so there's a clear record.
              </p>
              <div className="mt-3 flex flex-wrap gap-2">
                <button
                  type="button"
                  onClick={() => startEditing(true, aiSuggests ? "AI inspection flagged possible damage — please review." : "")}
                  className="inline-flex items-center gap-1.5 whitespace-nowrap rounded-md bg-secondary-500 px-3.5 py-2 text-xs font-medium text-background-50 transition-colors hover:bg-secondary-600"
                >
                  <i className="ri-alert-line"></i> Flag damage
                </button>
                <button
                  type="button"
                  onClick={() => startEditing(false, "Compared both walkarounds — no change in condition.")}
                  className="inline-flex items-center gap-1.5 whitespace-nowrap rounded-md border border-background-200 bg-background-50 px-3.5 py-2 text-xs font-medium text-foreground-900 transition-colors hover:border-primary-500/60"
                >
                  <i className="ri-shield-check-line"></i> Mark all clear
                </button>
              </div>
            </>
          )}
        </div>
      )}

      {editing && (
        <div className="mt-3">
          <div className="flex flex-wrap gap-2">
            <button
              type="button"
              onClick={() => setDraftFlagged(true)}
              className={`inline-flex items-center gap-1.5 whitespace-nowrap rounded-full px-3.5 py-1.5 text-xs font-medium transition-colors ${
                draftFlagged ? "bg-secondary-500 text-background-50" : "border border-background-200 bg-background-50 text-foreground-700"
              }`}
            >
              <i className="ri-alert-line"></i> Damage flagged
            </button>
            <button
              type="button"
              onClick={() => setDraftFlagged(false)}
              className={`inline-flex items-center gap-1.5 whitespace-nowrap rounded-full px-3.5 py-1.5 text-xs font-medium transition-colors ${
                !draftFlagged ? "bg-accent-500 text-background-50" : "border border-background-200 bg-background-50 text-foreground-700"
              }`}
            >
              <i className="ri-shield-check-line"></i> All clear
            </button>
          </div>

          <textarea
            value={draftNote}
            onChange={(event) => setDraftNote(event.target.value.slice(0, MAX_NOTE))}
            rows={3}
            maxLength={MAX_NOTE}
            placeholder="e.g. New scratch on the front-left alloy, wasn't there at pickup. Photos at 0:42 in the return clip."
            className="mt-3 w-full resize-none rounded-xl border border-background-200/70 bg-background-50 px-3.5 py-3 text-sm text-foreground-950 placeholder:text-foreground-500 focus:border-primary-500 focus:outline-none"
          />

          {saveError && (
            <p className="mt-2 flex items-center gap-2 rounded-lg border border-secondary-700/40 bg-secondary-950/40 px-3 py-2 text-xs text-secondary-300">
              <i className="ri-error-warning-line"></i> {saveError}
            </p>
          )}

          <div className="mt-3 flex flex-wrap justify-end gap-2">
            <button
              type="button"
              onClick={() => setEditing(false)}
              disabled={saving}
              className="inline-flex items-center gap-1.5 whitespace-nowrap rounded-md border border-background-200 bg-background-50 px-3.5 py-2 text-xs font-medium text-foreground-900 transition-colors hover:border-primary-500/60 disabled:opacity-50"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={commit}
              disabled={saving}
              className="inline-flex items-center gap-1.5 whitespace-nowrap rounded-md bg-primary-500 px-3.5 py-2 text-xs font-medium text-background-50 transition-colors hover:bg-primary-600 disabled:opacity-50"
            >
              {saving ? <i className="ri-loader-4-line animate-spin"></i> : <i className="ri-save-3-line"></i>}
              {saving ? "Saving…" : "Save verdict"}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}